import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { ClienteListItem } from './ClienteListItem';
import { InboxAutoServicePopover } from './InboxAutoServicePopover';
import { useClientes } from '@/hooks/useClientes';
import { useInboxPreviews, useRealtimeInbox } from '@/hooks/useMensagens';
import { useClientesOSIndicador, useRealtimeOSIndicador } from '@/hooks/useOrdensServico';
import { useAuthContext } from '@/contexts/AuthContext';
import { Database } from '@/integrations/supabase/types';

type StatusCliente = Database['public']['Enums']['status_cliente'];

interface ClienteRow {
  id: string;
  nome: string | null;
  avatar_url: string | null;
  telefone: string | null;
  codigo_cliente: string | null;
  status: StatusCliente | null;
  responsavel_id: string | null;
}

interface ClientesListProps {
  clienteSelecionadoId?: string;
  onSelectCliente: (id: string) => void;
}

export function ClientesList({ clienteSelecionadoId, onSelectCliente }: ClientesListProps) {
  const { t } = useTranslation();
  const [termoBusca, setTermoBusca] = useState('');
  const { data: clientes, isLoading } = useClientes();
  const { isAdmin, profile, organizationId } = useAuthContext();

  const clientesFiltrados = useMemo(() => {
    if (!clientes) return [];

    let lista = clientes as unknown as ClienteRow[];

    if (!isAdmin && profile?.id) {
      // Atendente vê os seus clientes + os não-atribuídos (fila para pegar).
      // Admin (ramo não tocado) continua a ver todos.
      lista = lista.filter(
        (c) => c.responsavel_id === profile.id || c.responsavel_id === null,
      );
    }

    if (termoBusca.trim()) {
      const termo = termoBusca.toLowerCase();
      lista = lista.filter(
        (c) =>
          c.nome?.toLowerCase().includes(termo) ||
          c.telefone?.includes(termo),
      );
    }

    return lista;
  }, [clientes, termoBusca, isAdmin, profile?.id]);

  const clienteIds = useMemo(() => clientesFiltrados.map((c) => c.id), [clientesFiltrados]);
  const { data: previews } = useInboxPreviews(clienteIds);
  const { data: osIndicadores } = useClientesOSIndicador(clienteIds);
  // Mantém o indicador da OS fresco quando o prestador muda o estado noutro dispositivo.
  useRealtimeOSIndicador();
  useRealtimeInbox(organizationId);

  const novosCount = useMemo(
    () => clientesFiltrados.filter((c) => c.status === 'novo').length,
    [clientesFiltrados],
  );

  if (isLoading) {
    return (
      <>
        <div className="painel__list-hdr">
          <div className="painel__list-title">{t('panel.inbox')}</div>
        </div>
        <div className="painel__search">
          <Skeleton className="h-10 w-full rounded-full" />
        </div>
        <div className="painel__list-body">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-3 px-3 py-2">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="flex-1">
                <Skeleton className="h-3.5 w-32 mb-1.5" />
                <Skeleton className="h-3 w-48" />
              </div>
            </div>
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="painel__list-hdr">
        <div className="painel__list-title">
          {t('panel.inbox')}
          {novosCount > 0 && (
            <span className="painel__list-new">
              {novosCount} {t('panel.newLabel', 'Novos')}
            </span>
          )}
        </div>
        <div className="painel__list-icons">
          <InboxAutoServicePopover />
        </div>
      </div>

      <div className="painel__search">
        <label className="painel__search-input">
          <Search size={14} style={{ color: 'hsl(var(--muted-foreground))' }} />
          <input
            type="text"
            placeholder={t('panel.searchClient')}
            value={termoBusca}
            onChange={(e) => setTermoBusca(e.target.value)}
          />
        </label>
      </div>

      <div className="painel__list-body">
        {clientesFiltrados.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-muted-foreground">
            {t('panel.noClientsFound')}
          </div>
        ) : (
          clientesFiltrados.map((cliente) => {
            const preview = previews?.[cliente.id];
            return (
              <ClienteListItem
                key={cliente.id}
                id={cliente.id}
                nome={cliente.nome || t('common.noName')}
                avatarUrl={cliente.avatar_url}
                codigo={cliente.codigo_cliente}
                osIndicador={osIndicadores?.[cliente.id] ?? null}
                preview={preview?.conteudo}
                previewTipoMedia={preview?.tipo_media ?? null}
                previewTime={preview?.criado_em}
                unreadCount={preview?.unread_count ?? 0}
                isSelected={cliente.id === clienteSelecionadoId}
                onClick={() => onSelectCliente(cliente.id)}
              />
            );
          })
        )}
      </div>
    </>
  );
}
